import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import Select from "react-select";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import { logout } from "../../app/auth/authSlice.js";
import DropDownStyles, {
  DropDownStylesDark,
  defaultSelectPortalProps,
} from "../staff/dropdownStyles.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar.jsx";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter.jsx";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState.jsx";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader.jsx";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles.js";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader.js";

const ManageCompany = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );

  const themeColor = useThemeColor();

  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [sortBy, setSortBy] = useState({
    value: "CompID",
    label: "Company ID",
  });
  const [sortOrder, setSortOrder] = useState({ value: "DESC", label: "Desc" });
  const [companyType, setCompanyType] = useState({
    value: "",
    label: "All Types",
  });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoLoginCompany, setAutoLoginCompany] = useState(null);

  const companyTypeOptions = [
    { value: "", label: "All Types" },
    { value: "Hospitality", label: "Hospitality" },
    { value: "Retail", label: "Retail" },
    { value: "Saloon", label: "Saloon" },
  ];

  const FetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_COMPANIES, {
        params: {
          type: companyType.value,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search.trim() || "",
          PageSize: pageSize.value,
          PageNumber: pageNumber,
        },
      });

      if (response.data.status) {
        const companyData =
          response.data.data?.companies ?? response.data.data?.Companies;

        if (Array.isArray(companyData)) {
          setCompanies(companyData);
          const data = response.data.data || {};
          const total =
            data.totalRecords ??
            data.totalRecord ??
            data.TotalRecord ??
            response.data.totalRecords ??
            companyData.length;
          const pages =
            data.totalPages ??
            data.pageCount ??
            data.PageCount ??
            response.data.totalPages ??
            Math.ceil(total / (Number(pageSize.value) || 1));

          setTotalRecords(total);
          setTotalPages(pages || 1);
        } else {
          console.error("Unexpected API response format:", response.data);
          setCompanies([]);
          setTotalRecords(0);
          setTotalPages(1);
        }
      } else {
        const apiMessage =
          response.data?.message ||
          response.data?.Message ||
          "Failed to fetch companies";
        showErrorToast(apiMessage);
        setCompanies([]);
        setTotalRecords(0);
        setTotalPages(1);
      }

      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 0);
      } else {
        showErrorToast("Failed to fetch companies. Please try again.");
        console.error("Error fetching companies:", err);
      }
    }
  }, [
    search,
    pageSize.value,
    sortBy.value,
    sortOrder.value,
    companyType.value,
    pageNumber,
  ]);

  const DeleteCompany = async (company) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${
        company.compName || "this company"
      }?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.delete(APIs.DELETE_COMPANY, {
        params: {
          CompID: company.compID,
        },
      });

      if (response.data.status) {
        setCompanies((prevCompanies) =>
          prevCompanies.filter(
            (delCompany) => delCompany.compID !== company.compID
          )
        );
        showSuccessToast("Company is Deleted.");

        if (companies.length === 1 && pageNumber > 1) {
          setPageNumber((prev) => prev - 1);
        }
      } else {
        showErrorToast(response.data.message || "Failed to delete company");
      }
    } catch (err) {
      showErrorToast("Something went wrong while deleting company.");
      console.error("Error deleting company:", err);
    } finally {
      setLoading(false);
    }
  };

  // Auto-Login: open confirm modal
  const handleAutoLogin = (company) => {
    setAutoLoginCompany(company);
  };

  const closeAutoLoginModal = () => {
    if (loading) return;
    setAutoLoginCompany(null);
  };

  // Auto-Login: generate token and open BackOffice after confirm
  const confirmAutoLogin = async () => {
    if (!autoLoginCompany) return;

    const company = autoLoginCompany;
    setAutoLoginCompany(null);

    try {
      setLoading(true);

      const response = await axiosInstance.post(APIs.GENERATE_AUTO_LOGIN_TOKEN, {
        CompID: company.compID,
      });

      if (response.data.status && response.data.data.token) {
        const token = response.data.data.token;
        const backOfficeUrl =
          process.env.REACT_APP_BACKOFFICE_URL || "http://localhost:3001";
        const autoLoginUrl = `${backOfficeUrl}/auto-login?token=${token}&compId=${company.compID}`;
        window.open(autoLoginUrl, "_blank");
        showSuccessToast(`Auto-login initiated for ${company.compName}`);
      } else {
        showErrorToast(
          response.data.message || "Failed to generate auto-login token"
        );
      }
    } catch (err) {
      console.error("Auto-login error:", err);
      if (err.response?.status === 401) {
        showErrorToast("Unauthorized. Please login again.");
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast(
          err.response?.data?.message ||
            "Failed to initiate auto-login. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRowClick = (compID) => {
    navigate(`/company/updateCompany/${compID}`);
  };

  const handleSort = (field) => {
    if (sortBy.value === field) {
      setSortOrder((prev) =>
        prev.value === "ASC"
          ? { value: "DESC", label: "Desc" }
          : { value: "ASC", label: "Asc" }
      );
      return;
    }
    setSortBy({ value: field, label: field });
    setSortOrder({ value: "ASC", label: "Asc" });
  };

  const clearFilters = () => {
    setSearch("");
    setCompanyType({ value: "", label: "All Types" });
  };

  const hasFiltersApplied = () =>
    search.trim() !== "" || companyType.value !== "";

  useEffect(() => {
    setPageNumber(1);
  }, [search, pageSize.value, sortBy.value, sortOrder.value, companyType.value]);

  useEffect(() => {
    const handler = setTimeout(() => {
      FetchCompanies();
    }, 500);

    return () => clearTimeout(handler);
  }, [FetchCompanies]);

  const renderNoDataMessage = () => {
    if (loading || companies.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No companies found"
        description={
          hasFiltersApplied()
            ? "No companies match your current search or type filter. Try different filters or clear them to see all companies."
            : "There are no companies to show right now. Add a new company or check back later."
        }
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  const actionButtonStyle = (color) => ({
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "6px",
    border: `1px solid ${color}`,
    color,
    transition: "all 0.2s ease",
  });

  const formatCompanyExpireDate = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime()) || date.getFullYear() < 1900) return "N/A";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const isCompanyExpired = (value) => {
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime()) || date.getFullYear() < 1900) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Company</li>
          </ol>
          <h1 className="page-header mb-0">Manage Company</h1>
        </div>

        <div className="ms-auto">
          <Link to="/company/addCompany">
            <button
              className="btn px-4"
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: "none",
                color: "white",
                borderRadius: "8px",
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: "600",
                transition: "all 0.3s ease",
                boxShadow: `0 2px 8px ${themeColor}40`,
              }}
              onMouseEnter={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                e.target.style.transform = "translateY(-1px)";
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
              Add Company
            </button>
          </Link>
        </div>
      </div>

      <div className="mb-3 mt-2">
        <ModernGridToolbar
          isDarkMode={isDarkMode}
          title="Companies"
          totalCount={totalRecords}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by name, email, phone..."
          bodyFlush
          topFilters={
            <ModernGridToolbarFilterPill
              iconClass="fa fa-building"
              className="modern-grid-toolbar__filter-pill--status"
            >
              <Select
                value={companyType}
                onChange={setCompanyType}
                options={companyTypeOptions}
                styles={filterPillSelectStyles}
                placeholder="All Types"
                isSearchable={false}
                {...defaultSelectPortalProps}
              />
            </ModernGridToolbarFilterPill>
          }
        >
          <div className="px-3 pb-3">
            <GridSectionLoader loading={loading} minHeight="280px">
              {renderNoDataMessage()}

              {companies.length > 0 && (
                <>
                  <div
                    className="table-responsive table-container"
                    style={{ minHeight: "200px", position: "relative", zIndex: 1 }}
                  >
                    <table
                      className="table table-hover text-nowrap"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead>
                        <tr>
                          <SortableColumnHeader
                            sortField="CompID"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                          >
                            ID
                          </SortableColumnHeader>
                          <SortableColumnHeader
                            sortField="CompName"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                          >
                            Name
                          </SortableColumnHeader>
                          <SortableColumnHeader
                            sortField="CompEmail"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                          >
                            Email
                          </SortableColumnHeader>
                          <SortableColumnHeader
                            sortField="CompPhone"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                          >
                            Phone
                          </SortableColumnHeader>
                          <SortableColumnHeader
                            sortField="CompIndustryType"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                          >
                            Type
                          </SortableColumnHeader>
                          <SortableColumnHeader
                            sortField="CompCity"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                          >
                            City
                          </SortableColumnHeader>
                          <SortableColumnHeader
                            sortField="CompCurrency"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                          >
                            Currency
                          </SortableColumnHeader>
                          <SortableColumnHeader
                            sortField="CompExpireDate"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                          >
                            Expiry Date
                          </SortableColumnHeader>
                          <th className="pt-2 pb-2 text-center">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companies.map((company, index) => (
                          <tr key={company.compID || index}>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(company.compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              {company.compID || "N/A"}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(company.compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              {company.compName || "N/A"}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(company.compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              {company.compEmail || "N/A"}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(company.compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              {company.compPhone || "N/A"}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(company.compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              {company.compIndustryType || "N/A"}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(company.compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              {company.compCity || "N/A"}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(company.compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              {company.compCurrency || "N/A"}
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleRowClick(company.compID)}
                              style={{ cursor: "pointer" }}
                              title="Click to edit"
                            >
                              <span
                                style={{
                                  color: isCompanyExpired(company.compExpireDate)
                                    ? "#dc3545"
                                    : undefined,
                                  fontWeight: isCompanyExpired(company.compExpireDate)
                                    ? 600
                                    : undefined,
                                }}
                              >
                                {formatCompanyExpireDate(company.compExpireDate)}
                              </span>
                            </td>
                            <td className="align-middle text-center">
                              <div className="d-flex justify-content-center gap-2">
                                <button
                                  onClick={() => handleAutoLogin(company)}
                                  className="btn btn-sm btn-outline-success"
                                  title="Auto-Login to BackOffice"
                                  disabled={loading}
                                  style={actionButtonStyle("#28a745")}
                                >
                                  <i
                                    className="fa fa-sign-in-alt"
                                    style={{ fontSize: "12px" }}
                                  ></i>
                                </button>

                                <Link
                                  to={`/company/updateCompany/${company.compID}`}
                                  className="btn btn-sm btn-outline-primary"
                                  title="Edit Company"
                                  style={actionButtonStyle(themeColor)}
                                >
                                  <i
                                    className="fa fa-edit"
                                    style={{ fontSize: "12px" }}
                                  ></i>
                                </Link>

                                <button
                                  onClick={() => DeleteCompany(company)}
                                  className="btn btn-sm btn-outline-danger"
                                  title="Delete Company"
                                  disabled={loading}
                                  style={actionButtonStyle("#dc3545")}
                                >
                                  <i
                                    className="fa fa-trash"
                                    style={{ fontSize: "12px" }}
                                  ></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <ModernGridPagerFooter
                    pageSize={pageSize}
                    onPageSizeChange={setPageSize}
                    totalRecords={totalRecords}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                  />
                </>
              )}
            </GridSectionLoader>
          </div>
        </ModernGridToolbar>
      </div>

      <CustomModal
        title="Open BackOffice?"
        subtitle={
          autoLoginCompany
            ? `Sign in to ${
                autoLoginCompany.compName || "this company"
              } as a secure auto-login session.`
            : ""
        }
        fontSize="18px"
        fontWeight={600}
        onClose={closeAutoLoginModal}
        isOpen={!!autoLoginCompany}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Continue"
        onSubmit={confirmAutoLogin}
        modalSize="modal-dialog-centered"
        minHeight="auto"
        maxHeight="420px"
      >
        {autoLoginCompany ? (
          <div className="px-1 pb-1">
            <div
              className="d-flex align-items-start gap-3 p-3 rounded-3"
              style={{
                backgroundColor: isDarkMode ? "#1e2427" : "#f0fdf9",
                border: `1px solid ${isDarkMode ? "#3a4044" : "#bbf7d0"}`,
              }}
            >
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0 rounded-circle"
                style={{
                  width: 44,
                  height: 44,
                  backgroundColor: "#28a74522",
                  color: "#28a745",
                }}
              >
                <i className="fa fa-sign-in-alt" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <div
                  className="fw-semibold text-break"
                  style={{
                    color: isDarkMode ? "#f1f5f9" : "#0f172a",
                    fontSize: 15,
                  }}
                >
                  {autoLoginCompany.compName || "Selected company"}
                </div>
                <div
                  className="small mt-1"
                  style={{
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                    lineHeight: 1.45,
                  }}
                >
                  ID #{autoLoginCompany.compID}
                  {autoLoginCompany.compEmail
                    ? ` · ${autoLoginCompany.compEmail}`
                    : ""}
                </div>
                <p
                  className="small mb-0 mt-2"
                  style={{
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                    lineHeight: 1.45,
                  }}
                >
                  BackOffice will open in a new browser tab. Keep this Super Admin
                  session open if you need to return here.
                </p>
              </div>
            </div>
          </div>
        ) : null}
      </CustomModal>
    </div>
  );
};

export default ManageCompany;
